import { useEffect, useContext, useEffectEvent } from 'react';

import useSnackbar from './useSnackbar';
import { ClinicRxContext } from '../../App';
import {
  SET_ALERT_DATA,
  UPDATE_DISCHARGE_DATE,
  CHANGE_APPOINTMENT_DATE,
  UPDATE_ALERT_STATUS,
  UPDATE_ALERT_STATUS_AND_APPOINTMENT,
} from '../../dispatchActionTypes';
import { SocketContext } from '../SocketProvider';
import { alertStatusMap } from '../../components/dashboard';
import { addCalculatedColumns } from '../../components/custom';

const INCOMING_ALERT = 'incoming-alert';
const ALERT_STATUS_UPDATE = 'alert-status-update';
const STATUS_UPDATE_WITH_APPOINTMENT = 'status-update-with-appointment';
const CHANGED_APPOINTMENT_DATE = 'changed-appointment-date';
const CHANGED_DISCHARGE_DATE = 'changed-discharge-date';

const UNCLAIM_ALERT = 'unclaim-alert';
const UPDATE_AND_SCHEDULE = 'update-and-schedule';
const DISCHARGE_DATE_CHANGED = 'discharge-date-changed';
const APPOINTMENT_DATE_CHANGED = 'appointment-date-changed';

export default function useRTData() {
  const { state, dispatch } = useContext(ClinicRxContext);
  const { socket, snackbar } = useContext(SocketContext);
  const { showSnackbarIfRelevant } = useSnackbar();

  const handleIncomingAlert = (alertData) => {
    const { newAlert } = JSON.parse(alertData);
    const withCCs = addCalculatedColumns(newAlert, state.user.organization);
    dispatch({
      type: SET_ALERT_DATA,
      payload: {
        newAlert: withCCs,
      },
    });
    let verb = 'received';
    if (newAlert.facilityContact.facilityContactEmail === state.user.email) {
      verb = 'created';
    }
    snackbar.setIsOpen(true);
    snackbar.setMessage(`You ${verb} a new alert #${newAlert.alertID}!`);
  };

  const handleDischargeDateChange = (dischargeData) => {
    console.log(
      'the front end received a socket discharge date change notification',
      dischargeData,
    );

    const { alertID, updatedDischargeDate, historyEntry } =
      JSON.parse(dischargeData);

    showSnackbarIfRelevant(
      alertID,
      `Alert #${alertID} discharge date changed to ${updatedDischargeDate}.`,
    );

    dispatch({
      type: UPDATE_DISCHARGE_DATE,
      payload: {
        alertID,
        updatedDischargeDate,
        historyEntry,
      },
    });
  };

  const handleChangedAppointmentDate = (updateData) => {
    console.log(
      'the front end received a socket changed appointment date notification',
      updateData,
    );

    const { alertID, changedAppointmentDate, historyEntry } =
      JSON.parse(updateData);

    showSnackbarIfRelevant(
      alertID,
      `Alert #${alertID} appointment date changed to ${changedAppointmentDate}.`,
    );

    dispatch({
      type: CHANGE_APPOINTMENT_DATE,
      payload: {
        alertID,
        changedAppointmentDate,
        historyEntry,
      },
    });
  };

  const handleAlertStatusUpdate = (updateData) => {
    console.log(
      'the front end received a socket alert status update notification:',
      updateData,
    );

    const {
      alertID,
      updatedAlertStatus,
      historyEntry,
      shouldRemoveFromDashboard,
      unclaimedClinics,
      isAlertNowUnrouteable = false,
      // Tree only set to
      // false on socket when
      // unclaiming.
      isTreeOnDashboard = true,
    } = JSON.parse(updateData);

    const snackbarMessage = isAlertNowUnrouteable
      ? `Alert #${alertID} is unclaimed and now unrouteable.`
      : `Alert #${alertID} is ${alertStatusMap[updatedAlertStatus].toLowerCase()}!`;
    showSnackbarIfRelevant(alertID, snackbarMessage);

    dispatch({
      type: UPDATE_ALERT_STATUS,
      payload: {
        alertID,
        nextAlertStatus: updatedAlertStatus,
        historyEntry,
        shouldRemoveFromDashboard,
        unclaimedClinics,
        isTreeOnDashboard,
      },
    });
  };

  const handleStatusUpdateWithAppointment = (alertData) => {
    console.log(
      'the front end received a socket update and schedule notification:',
      alertData,
    );

    const {
      alertID,
      alertStatus,
      historyEntry,
      appointmentDate,
      historyEntryAppointmentDate,
    } = JSON.parse(alertData);

    showSnackbarIfRelevant(
      alertID,
      `Alert #${alertID} scheduled on ${appointmentDate}.`,
    );

    dispatch({
      type: UPDATE_ALERT_STATUS_AND_APPOINTMENT,
      payload: {
        alertID,
        alertStatus,
        historyEntry,
        appointmentDate,
        historyEntryAppointmentDate,
      },
    });
  };

  const onIncomingAlert = useEffectEvent(handleIncomingAlert);
  const onDischargeDateChange = useEffectEvent(handleDischargeDateChange);
  const onChangedAppointmentDate = useEffectEvent(handleChangedAppointmentDate);
  const onAlertStatusUpdate = useEffectEvent(handleAlertStatusUpdate);
  const onStatusUpdateWithAppointment = useEffectEvent(
    handleStatusUpdateWithAppointment,
  );

  useEffect(() => {
    socket.on(INCOMING_ALERT, onIncomingAlert);
    socket.on(CHANGED_DISCHARGE_DATE, onDischargeDateChange);
    socket.on(CHANGED_APPOINTMENT_DATE, onChangedAppointmentDate);
    socket.on(ALERT_STATUS_UPDATE, onAlertStatusUpdate);
    socket.on(STATUS_UPDATE_WITH_APPOINTMENT, onStatusUpdateWithAppointment);
    return () => {
      socket.off(INCOMING_ALERT, onIncomingAlert);
      socket.off(CHANGED_DISCHARGE_DATE, onDischargeDateChange);
      socket.off(CHANGED_APPOINTMENT_DATE, onChangedAppointmentDate);
      socket.off(ALERT_STATUS_UPDATE, onAlertStatusUpdate);
      socket.off(STATUS_UPDATE_WITH_APPOINTMENT, onStatusUpdateWithAppointment);
    };
  }, []);

  const emitUnclaim = (alertData) => {
    socket.emit(UNCLAIM_ALERT, JSON.stringify(alertData), () => {
      console.log('Unclaim alert data successfully sent to the socket');
    });
  };

  const emitUpdateAndSchedule = (alertData) => {
    socket.emit(UPDATE_AND_SCHEDULE, JSON.stringify(alertData), () => {
      console.log('Update and schedule data successfully sent to the socket');
    });
  };

  const emitUpdateStatus = (alertData) => {
    socket.emit(UPDATE_ALERT_STATUS, JSON.stringify(alertData), () => {
      console.log('Update alert status data successfully sent to the socket');
    });
  };

  const emitDischargeDateChange = (alertData) => {
    socket.emit(DISCHARGE_DATE_CHANGED, JSON.stringify(alertData), () => {
      console.log('Changed discharge date successfully sent to the socket');
    });
  };

  const emitAppointmentDateChange = (alertData) => {
    socket.emit(APPOINTMENT_DATE_CHANGED, JSON.stringify(alertData), () => {
      console.log('Updated appointment date successfully sent to the socket');
    });
  };

  return {
    emitterFns: {
      emitUnclaim,
      emitUpdateAndSchedule,
      emitUpdateStatus,
      emitDischargeDateChange,
      emitAppointmentDateChange,
    },
  };
}
